import {
  Component,
  input,
  computed,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type PillSoonAnimation = 'none' | 'pulse' | 'shimmer' | 'glow' | 'swing';

export type PillSoonSize = 'small' | 'medium';

/**
 * PillSoon component for displaying "coming soon" or status badges with animations
 * 
 * @example
 * ```html
 * <ds-pill-soon 
 *   [textValue]="'Muy Pronto'"
 *   [backgroundColorValue]="'#D6D9E0'"
 *   [textColorValue]="'#7E808F'"
 *   [animationValue]="'shimmer'"
 * ></ds-pill-soon>
 * ```
 */
@Component({
  selector: 'ds-pill-soon',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pill-soon.component.html',
  styleUrls: ['./pill-soon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PillSoonComponent {
  textValue = input<string>('Muy Pronto');
  backgroundColorValue = input<string>('#D6D9E0');
  textColorValue = input<string>('#7E808F');
  animationValue = input<PillSoonAnimation>('shimmer');
  sizeValue = input<PillSoonSize>('small');
  animationEnabledValue = input<boolean>(true);
  customClassValue = input<string>('');

  private isCssClass(value: string): boolean {
    // Check if it starts with known class prefixes or contains class-like patterns
    return /^(bg-|text-|border-|from-|to-|via-|shadow-|ring-|backdrop-|fill-|stroke-)|\s/.test(value) && 
           !(/^(#[0-9a-f]{3,8}|rgb|rgba|hsl|hsla|transparent|inherit|initial|unset|var\()/i.test(value));
  }

  private getColorClasses = computed(() => {
    const bgColor = this.backgroundColorValue();
    const textColor = this.textColorValue();
    
    return {
      backgroundClass: this.isCssClass(bgColor) ? bgColor : '',
      textClass: this.isCssClass(textColor) ? textColor : '',
      hasBackgroundClass: this.isCssClass(bgColor),
      hasTextClass: this.isCssClass(textColor)
    };
  });

  pillClasses = computed(() => {
    const baseClasses = ['pill-soon'];
    const animation = this.animationValue();
    const size = this.sizeValue();
    const customClasses = this.customClassValue();
    const animationEnabled = this.animationEnabledValue();
    const colorClasses = this.getColorClasses();

    baseClasses.push(`pill-soon--${size}`);

    if (animationEnabled && animation !== 'none') {
      baseClasses.push(`pill-soon--${animation}`);
    }

    // Add Tailwind color classes if they are CSS classes
    if (colorClasses.hasBackgroundClass) {
      baseClasses.push(colorClasses.backgroundClass);
    }
    
    if (colorClasses.hasTextClass) {
      baseClasses.push(colorClasses.textClass);
    }

    if (customClasses) {
      baseClasses.push(customClasses);
    }

    return baseClasses.join(' ');
  });

  textClasses = computed(() => {
    const baseClasses = ['pill-soon__text'];
    const size = this.sizeValue();

    baseClasses.push(`pill-soon__text--${size}`);

    return baseClasses.join(' ');
  });

  pillStyles = computed(() => {
    const backgroundColor = this.backgroundColorValue();
    const textColor = this.textColorValue();
    const colorClasses = this.getColorClasses();
    
    const styles: { [key: string]: string } = {};
    
    // Only apply inline styles if not using CSS classes
    if (!colorClasses.hasBackgroundClass) {
      styles['--pill-bg-color'] = backgroundColor;
      styles['backgroundColor'] = backgroundColor;
    }
    
    if (!colorClasses.hasTextClass) {
      styles['--pill-text-color'] = textColor;
      styles['color'] = textColor;
    }
    
    return styles;
  });

  hasText = computed(() => this.textValue().trim() !== '');

  isAnimated = computed(() => this.animationEnabledValue() && this.animationValue() !== 'none');
}